import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Stack, Table, Button, Input, Select, Option, Checkbox } from '@mui/joy';
import { lookupsService } from '../services/lookups.service';

type FilterKind = 'materials' | 'equipment' | 'labour';

export const LookupAdminPanel: React.FC<{ initialFilter?: FilterKind; locked?: boolean; onDataChanged?: () => void; onSaved?: (kind: FilterKind, item: any) => void }> = ({ initialFilter, locked, onDataChanged, onSaved }) => {
  const [materials, setMaterials] = useState<any[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [labourRates, setLabourRates] = useState<any[]>([]);
  const [filter, setFilter] = useState<FilterKind>(initialFilter || 'materials');

  const [editing, setEditing] = useState<any | null>(null);
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [supplier, setSupplier] = useState('');
  const [unit, setUnit] = useState('');
  const [cost, setCost] = useState<number>(0);
  const [vatInclusive, setVatInclusive] = useState<boolean>(false);
  const [rateType, setRateType] = useState<'hour' | 'day'>('hour');

  const refresh = async () => {
    const [m, e, l] = await Promise.all([
      lookupsService.listMaterials(),
      lookupsService.listEquipment(),
      lookupsService.listLabourRates(),
    ]);
    setMaterials(m as any); setEquipment(e as any); setLabourRates(l as any);
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const beginEdit = (item: any) => {
    setEditing(item);
    setName(item.name || '');
    setSku(item.sku || '');
    setSupplier(item.supplier_name || '');
    setUnit(item.default_unit || '');
    setCost(Number(item.default_cost || item.rate || 0));
    setRateType(item.rate_type || 'hour');
    setVatInclusive(!!item.vat_inclusive);
  };

  const save = async () => {
    if (!editing) return;
    if (filter === 'materials') {
      const saved = await lookupsService.upsertMaterial({ id: editing.id, name, sku: sku || null, supplier_name: supplier || null, default_unit: unit || null, default_cost: cost, vat_inclusive: vatInclusive });
      onSaved?.('materials', saved);
    } else if (filter === 'equipment') {
      const saved = await lookupsService.upsertEquipment({ id: editing.id, name, sku: sku || null, supplier_name: supplier || null, default_unit: unit || null, default_cost: cost, vat_inclusive: vatInclusive });
      onSaved?.('equipment', saved);
    } else {
      const saved = await lookupsService.upsertLabourRate({ id: editing.id, name, rate_type: rateType, rate: cost });
      onSaved?.('labour', saved);
    }
    setEditing(null); setName(''); setSku(''); setSupplier(''); setUnit(''); setCost(0); setVatInclusive(false);
    await refresh();
    onDataChanged?.();
  };

  const remove = async (item: any) => {
    if (filter === 'materials') await lookupsService.deleteMaterial(item.id);
    else if (filter === 'equipment') await lookupsService.deleteEquipment(item.id);
    else await lookupsService.deleteLabourRate(item.id);
    await refresh();
    onDataChanged?.();
  };

  const rows = filter === 'materials' ? materials : filter === 'equipment' ? equipment : labourRates;

  return (
    <Card sx={{ p: 2 }}>
      {!locked && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography level="title-md">Lookup Admin</Typography>
          <Select value={filter} onChange={(_, v) => v && setFilter(v as any)} size="sm" sx={{ minWidth: 160 }}>
            <Option value="materials">Materials</Option>
            <Option value="equipment">Equipment</Option>
            <Option value="labour">Labour Rates</Option>
          </Select>
        </Stack>
      )}
      <Table hoverRow>
        <thead>
          <tr>
            <th>{filter === 'labour' ? 'Name' : 'Item'}</th>
            {filter !== 'labour' && <th>SKU</th>}
            {filter !== 'labour' && <th>Supplier</th>}
            <th>{filter === 'labour' ? 'Rate Type' : 'Unit'}</th>
            <th>{filter === 'labour' ? 'Rate' : 'Default Cost'}</th>
            {filter !== 'labour' && <th>VAT inclusive?</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {/* Inline create row */}
          <tr>
            <td><Input placeholder={filter === 'labour' ? 'Name' : 'Item'} value={name} onChange={(e) => setName(e.target.value)} /></td>
            {filter !== 'labour' && <td><Input placeholder="SKU" value={sku} onChange={(e) => setSku(e.target.value)} /></td>}
            {filter !== 'labour' && <td><Input placeholder="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} /></td>}
            <td>
              {filter === 'labour' ? (
                <Select value={rateType} onChange={(_, v) => v && setRateType(v as 'hour' | 'day')} size="sm" sx={{ minWidth: 120 }}>
                  <Option value="hour">Hour</Option>
                  <Option value="day">Day</Option>
                </Select>
              ) : (
                <Input placeholder="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
              )}
            </td>
            <td><Input placeholder={filter === 'labour' ? 'Rate' : 'Default Cost'} type="number" value={String(cost)} onChange={(e) => setCost(Number(e.target.value || 0))} /></td>
            {filter !== 'labour' && <td><Checkbox label="" checked={vatInclusive} onChange={(e) => setVatInclusive(e.target.checked)} /></td>}
            <td>
              <Button size="sm" onClick={async () => {
                if (!name.trim()) return;
                if (filter === 'materials') {
                  await lookupsService.upsertMaterial({ name, sku: sku || null, supplier_name: supplier || null, default_unit: unit || null, default_cost: cost, vat_inclusive: vatInclusive });
                } else if (filter === 'equipment') {
                  await lookupsService.upsertEquipment({ name, sku: sku || null, supplier_name: supplier || null, default_unit: unit || null, default_cost: cost, vat_inclusive: vatInclusive });
                } else {
                  await lookupsService.upsertLabourRate({ name, rate_type: rateType, rate: cost });
                }
                setName(''); setSku(''); setSupplier(''); setUnit(''); setCost(0); setVatInclusive(false);
                await refresh();
              }}>Add</Button>
            </td>
          </tr>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td>{r.name}</td>
              {filter !== 'labour' && <td>{r.sku || '—'}</td>}
              {filter !== 'labour' && <td>{r.supplier_name || '—'}</td>}
              <td>{filter === 'labour' ? r.rate_type : (r.default_unit || '—')}</td>
              <td>{filter === 'labour' ? `£${Number(r.rate).toFixed(2)}` : `£${Number(r.default_cost).toFixed(2)}`}</td>
              {filter !== 'labour' && <td>{r.vat_inclusive ? 'Yes' : 'No'}</td>}
              <td>
                <Button size="sm" variant="outlined" onClick={() => beginEdit(r)}>Edit</Button>
                <Button size="sm" color="danger" variant="plain" onClick={() => remove(r)}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {editing && (
        <Box sx={{ mt: 2 }}>
          <Typography level="title-sm">Edit</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
            <Input placeholder={filter === 'labour' ? 'Name' : 'Item'} value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 220 }} />
            {filter !== 'labour' && (
              <>
                <Input placeholder="SKU" value={sku} onChange={(e) => setSku(e.target.value)} sx={{ minWidth: 160 }} />
                <Input placeholder="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} sx={{ minWidth: 180 }} />
                <Checkbox label="VAT inclusive?" checked={vatInclusive} onChange={(e) => setVatInclusive(e.target.checked)} />
                <Input placeholder="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} sx={{ minWidth: 120 }} />
              </>
            )}
            {filter === 'labour' && (
              <Select value={rateType} onChange={(_, v) => v && setRateType(v as 'hour' | 'day')} sx={{ minWidth: 120 }}>
                <Option value="hour">Hour</Option>
                <Option value="day">Day</Option>
              </Select>
            )}
            <Input placeholder={filter === 'labour' ? 'Rate' : 'Default Cost'} type="number" value={String(cost)} onChange={(e) => setCost(Number(e.target.value || 0))} sx={{ minWidth: 160 }} />
            <Button onClick={save}>Save</Button>
            <Button variant="plain" onClick={() => setEditing(null)}>Cancel</Button>
          </Stack>
        </Box>
      )}
    </Card>
  );
};

export default LookupAdminPanel;


